// packages import
import { Autocomplete, FormControl, Grid, TextField } from '@mui/material'
// component import
import { FC } from 'react'
import { InputFormFieldsProps } from '../../interfaceTypes'
import { CustomController } from '../common/CustomController'
import CustomPhoneController from '../common/CustomPhoneController'
import { Controller, useFormContext } from 'react-hook-form'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import ISO6391 from 'iso-639-1'

export const InputFormFields: FC<InputFormFieldsProps> = ({ isJudge, showExtension, isClient, mdSize = 4 }) => {
  const { control } = useFormContext()

  const languageOptions = ISO6391.getAllCodes().map(code => ISO6391.getName(code))
  return (
    <>
      <Grid item md={mdSize} sm={12} xs={12}>
        <CustomController controllerName="firstName" controllerLabel="First Name" fieldType="text" />
      </Grid>

      <Grid item md={mdSize} sm={12} xs={12}>
        <CustomController controllerName="middleName" controllerLabel="Middle Name" fieldType="text" />
      </Grid>

      <Grid item md={mdSize} sm={12} xs={12}>
        <CustomController controllerName="lastName" controllerLabel="Last Name" fieldType="text" />
      </Grid>

      <Grid item md={mdSize} sm={12} xs={12}>
        <CustomController controllerName="businessName" controllerLabel="Business Name" fieldType="text" />
      </Grid>

      <Grid item md={mdSize} sm={12} xs={12}>
        <CustomController controllerName="suffix" controllerLabel="Suffix" fieldType="text" />
      </Grid>

      <Grid item md={mdSize} sm={12} xs={12}>
        <CustomController controllerName="initials" controllerLabel="Initials" fieldType="text" upperCase />
      </Grid>

      <Grid item md={mdSize} sm={12} xs={12}>
        <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
          <Controller
            name="dateOfBirth"
            control={control}
            render={({ field, fieldState: { error, invalid } }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  {...field}
                  value={field.value ?? null}
                  disableFuture
                  label="Date of Birth"
                  renderInput={params => (
                    <TextField {...params} error={invalid} helperText={invalid ? error?.message : null} fullWidth />
                  )}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>
      </Grid>

      <Grid item md={isClient ? 6 : mdSize !== 4 ? mdSize : 12} sm={12} xs={12}>
        <CustomController controllerName="email" controllerLabel="Email" fieldType="email" />
      </Grid>

      {isClient && (
        <Grid item md={6} sm={12} xs={12}>
          <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={[
                    ...['English', 'Spanish'],
                    ...languageOptions
                      .filter(option => option !== 'English' && option !== 'Spanish')
                      .sort((a, b) => a.localeCompare(b))
                  ]}
                  value={languageOptions.find(option => option === field.value) || null}
                  onChange={(_, newValue) => field.onChange(newValue ?? '')}
                  getOptionLabel={option => (typeof option === 'string' ? option : '')}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={params => <TextField {...params} label="Select Language" variant="outlined" />}
                />
              )}
            />
          </FormControl>
        </Grid>
      )}

      <Grid item md={isJudge ? 6 : mdSize} sm={12} xs={12}>
        <CustomPhoneController
          controllerName="personalPhone"
          controllerLabel="Personal Phone"
          tooltip={
            isClient ? "This phone will be used to send Billing Text Messages. Make sure that it's accurate" : ''
          }
        />
      </Grid>

      {isJudge ? (
        <Grid item md={6} sm={12} xs={12}>
          <CustomController
            controllerName="phoneNumberExtension"
            controllerLabel="Extension"
            maxLength={6}
            isDisabled={!!!showExtension}
          />
        </Grid>
      ) : (
        <>
          <Grid item md={mdSize} sm={12} xs={12}>
            <CustomPhoneController controllerName="homePhone" controllerLabel="Home Phone" />
          </Grid>

          <Grid item md={mdSize} sm={12} xs={12}>
            <CustomPhoneController controllerName="phoneNumber" controllerLabel="Work Phone" />
          </Grid>
        </>
      )}
    </>
  )
}
